import {
  AddressField,
  BasicAddressDetails,
  BinarySelect,
  Checkbox,
  Client,
  ClientPersonalField,
  ClientOccupationField,
  DOG_BREEDS,
  DogField,
  Driver,
  DriverField,
  Email,
  FieldType,
  Form as IForm,
  NumberField,
  OCCUPANCY_TYPES,
  Password,
  Phone,
  POOL_TYPES,
  PoolField,
  Radio,
  ROOF_TYPES,
  RoofField,
  Select,
  Text,
  TextAreaField,
  InlineSelect,
} from '@insurgrid/common';
import { useMemo } from 'react';
import * as z from 'zod';
import { ZodFirstPartySchemaTypes, ZodType } from 'zod';
import { HiLockClosed } from '@react-icons/all-files/hi/HiLockClosed';
import { AiOutlinePhone } from '@react-icons/all-files/ai/AiOutlinePhone';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { Input, PhoneNumberInput } from './Input';
import { PasswordInput } from './PasswordInput';
import { FormSelect } from './Select';
import { ValidationMessage } from './constants';
import { Form } from './Form';
import { RadioGroup } from './RadioGroup';
import { Button } from '../Button';
import { FormBinarySelect } from './BinarySelect';
import { FormSearchableDropdown } from './FormSearchableDropdown';
import { AddressForm } from './AddressForm';
import { FormDriversInput } from './DriverForm';
import {
  ClientOccupationQuestionsForm,
  ClientPersonalQuestionsForm,
} from './ClientQuestionsForm';
import { FormTextArea } from './TextArea';
import { FormInlineSelect } from './InlineSelect';

interface FormRendererProps<
  DATA extends Record<string, any> = Record<string, any>,
> {
  form: IForm;
  onSubmit: (data: DATA) => Promise<void> | void;
  values?: DATA;
  color?: string;
  isLoading?: boolean;
  'data-test-id'?: string;
  submitButtonLabel?: string;
  formBodyClassName?: string;
  className?: string;
  serverErrors?: { path: string; message: string }[];
}

export const VALID_PHONE_SCHEMA = z
  .string()
  .regex(
    /^(\(?[2-9][0-8][0-9]\)? ?[2-9][0-9]{2}-?[0-9]{4})?$/,
    ValidationMessage.Phone,
  );

export const VALID_EMAIL_SCHEMA = z
  .string()
  .regex(/^([^\s@]+@[^\s@]+\.[^\s@]+)?$/, ValidationMessage.Email);

const controlsMap: Record<
  FieldType,
  (field: any, defaultValue?: any) => JSX.Element
> = {
  [FieldType.Text]: (fieldProps: Text | Password, defaultValue?: string) => (
    <Input
      {...fieldProps}
      defaultValue={defaultValue}
      data-test-id={fieldProps.name}
      leftIcon={HiLockClosed}
    />
  ),
  [FieldType.Password]: (fieldProps: Text | Password) => (
    <PasswordInput
      {...fieldProps}
      data-test-id={fieldProps.name}
      leftIcon={HiLockClosed}
    />
  ),
  [FieldType.Number]: (fieldProps: NumberField, defaultValue?: number) => (
    <Input
      {...fieldProps}
      type="number"
      data-test-id={fieldProps.name}
      defaultValue={defaultValue}
      onKeyDown={(e) => {
        if (/[a-zA-Z-.+]+/g.test(e.key) && e.key.length === 1) {
          e.preventDefault();
        }
      }}
    />
  ),
  [FieldType.Email]: (fieldProps: Text | Email) => (
    <Input
      {...fieldProps}
      data-test-id={fieldProps.name}
      leftIcon={HiLockClosed}
    />
  ),
  [FieldType.Select]: (field: Select) => (
    <FormSelect {...field} data-test-id={field.name} />
  ),
  [FieldType.Radio]: (field: Radio) => <RadioGroup {...field} />, // TODO
  [FieldType.Phone]: (field: Phone) => (
    <PhoneNumberInput
      label={field.label}
      name={field.name}
      formattingInState
      type="tel"
      leftIcon={AiOutlinePhone}
      format="(###) ###-####"
    />
  ),
  [FieldType.Checkbox]: (field: Checkbox) => <div />, // TODO
  [FieldType.AutocompleteAddress]: (
    field: AddressField,
    defaultValue?: BasicAddressDetails, // Should be { [key: string]: BasicAddressDetails }
  ) => <AddressForm {...field} defaultValue={defaultValue} />,
  [FieldType.Address]: (
    field: AddressField,
    defaultValue?: BasicAddressDetails, // Should be { [key: string]: BasicAddressDetails }
  ) => (
    <AddressForm {...field} defaultValue={defaultValue} autocomplete={false} />
  ),
  [FieldType.Driver]: (field: DriverField, defaultValue?: Driver) => (
    <FormDriversInput {...field} defaultValue={defaultValue} />
  ),
  [FieldType.BinarySelect]: (
    fieldProps: BinarySelect,
    defaultValue?: boolean,
  ) => <FormBinarySelect {...fieldProps} defaultValue={defaultValue} />,
  [FieldType.ClientPersonal]: (
    field: ClientPersonalField,
    defaultValue?: Client,
  ) => <ClientPersonalQuestionsForm {...field} defaultValue={defaultValue} />,
  [FieldType.ClientOccupation]: (
    field: ClientOccupationField,
    defaultValue?: Client,
  ) => <ClientOccupationQuestionsForm {...field} defaultValue={defaultValue} />,
  [FieldType.TextArea]: (field: TextAreaField, defaultValue?: string) => (
    <FormTextArea {...field} defaultValue={defaultValue} />
  ),
  [FieldType.Roof]: (field: RoofField, defaultValue?: string) => (
    <FormSearchableDropdown
      items={ROOF_TYPES}
      {...field}
      defaultValue={defaultValue}
      dropdownStyles={{ maxHeight: '9.75rem' }}
    />
  ),
  [FieldType.Dog]: (field: DogField, defaultValue?: string[]) => (
    <FormSearchableDropdown
      items={DOG_BREEDS}
      {...field}
      defaultValue={defaultValue}
      multiple
      dropdownStyles={{ maxHeight: '9.75rem' }}
    />
  ),
  [FieldType.Pool]: (field: PoolField, defaultValue?: string) => (
    <FormSearchableDropdown
      items={POOL_TYPES}
      {...field}
      defaultValue={defaultValue}
      dropdownStyles={{ maxHeight: '9.75rem' }}
    />
  ),
  [FieldType.Occupancy]: (field: PoolField, defaultValue?: string) => (
    <FormSearchableDropdown
      items={OCCUPANCY_TYPES}
      {...field}
      defaultValue={defaultValue}
      dropdownStyles={{ maxHeight: '9.75rem' }}
    />
  ),
  [FieldType.PingCode]: () => <></>,
  [FieldType.InlineSelect]: (field: InlineSelect) => <FormInlineSelect {...field} />,
};

function requiredNullableErrorHandling<T>(
  zodSchema: ZodFirstPartySchemaTypes,
  defaultValue: T,
) {
  return zodSchema.nullable().transform((value, ctx): T => {
    if (value === null || value === '') {
      ctx.addIssue({
        code: 'custom',
        message: ValidationMessage.Required,
      });
    }
    return value ?? defaultValue;
  });
}

const searchableDropdownSingleOptionSchema =
  requiredNullableErrorHandling<string>(
    z.string().min(1, ValidationMessage.Required),
    '',
  );
const searchableDropdownMultiOptionSchema = requiredNullableErrorHandling(
  z.array(searchableDropdownSingleOptionSchema).min(1).min(1),
  [],
);

const getZodRule = (fieldType: FieldType): z.ZodTypeAny => {
  switch (fieldType) {
    case FieldType.Address:
    case FieldType.AutocompleteAddress:
      return z.object({
        nickname: z.string().nullable().optional(),
        city: z.string().min(1, { message: 'City is required.' }),
        postal: z.string().min(1, { message: 'Zip code is required.' }),
        street: z.string().min(1, { message: 'Street is required.' }),
        street2: z.string().nullable().optional(),
        usStateCode: z.string().min(1, { message: 'State is required.' }),
      });
    case FieldType.Phone:
      return VALID_PHONE_SCHEMA;
    case FieldType.ClientPersonal:
      return z.object({
        phone: VALID_PHONE_SCHEMA.nullable().optional(),
        email: VALID_EMAIL_SCHEMA.nullable().optional(),
        gender: z.string().nullable().optional(),
        maritalStatus: z.string().nullable().optional(),
      });
    case FieldType.ClientOccupation:
      return z.object({
        occupation: z.string().nullable().optional(),
        industry: z.string().nullable().optional(),
        educationLevel: z.string().nullable().optional(),
      });
    case FieldType.Driver:
      return z.object({
        firstName: z.string().min(1, ValidationMessage.Required),
        lastName: z.string().min(1, ValidationMessage.Required),
        dateOfBirth: z
          .string()
          .min(1, ValidationMessage.Required)
          .refine((v) => {
            const dateOfBirth = dayjs(v.replace(/-/g, '/'), 'MM/DD/YYYY', true);
            const today = dayjs();
            return (
              !v.includes('_') &&
              dateOfBirth.isValid() &&
              dateOfBirth.isBefore(today.subtract(14, 'y')) &&
              dateOfBirth.isAfter(today.subtract(150, 'y'))
            );
          }, ValidationMessage.DateOfBirth),
        usStateCode: z.string().min(1, ValidationMessage.Required),
        driverLicenseCode: requiredNullableErrorHandling(z.string(), ''),
        // INS-1418. Must be listed even though it is optional: z.object strips
        // every key it does not name, so leaving it out drops the client's
        // answer before the request is built.
        relationshipToPolicyholder: z.string().nullable().optional(),
        //   // TODO abstract this function out to be a generic unsupported characters parser
        //   .transform((value, ctx) => {
        //   if (value.includes('*')) {
        //     ctx.addIssue({
        //       code: 'custom',
        //       message:
        //         'Driver\'s license number contains unsupported characters: "*"',
        //     });
        //   }
        //   return value ?? '';
        // }),
      });
    case FieldType.Number:
      return requiredNullableErrorHandling(
        z
          .string()
          .min(1, { message: ValidationMessage.Required })
          .transform((value, ctx) => {
            const isNaN = Number.isNaN(Number(value));
            if (isNaN) {
              ctx.addIssue({
                code: 'custom',
                message: 'Value should be a number.',
              });
            }
            return isNaN ? null : value;
          })
          .or(z.number().min(0)),
        0,
      );
    case FieldType.Email:
      return (
        z
          .string()
          .email(ValidationMessage.Email)
          // its 5 because the email has to be at the very least a@b.cd
          .min(5, { message: ValidationMessage.Email })
      );
    case FieldType.BinarySelect:
      return requiredNullableErrorHandling(z.boolean(), null);
    case FieldType.Roof:
    case FieldType.Pool:
    case FieldType.Occupancy:
      return searchableDropdownSingleOptionSchema;
    case FieldType.Dog:
      return searchableDropdownMultiOptionSchema;
    case FieldType.TextArea:
      return requiredNullableErrorHandling(
        z.string().min(1, { message: ValidationMessage.Required }),
        null,
      );
    default:
      return z.string().min(1, { message: ValidationMessage.Required });
  }
};

function ConditionalField({
  field,
  defaultValue,
}: {
  field: IForm[number];
  defaultValue?: any;
}) {
  const { watch } = useFormContext();
  const visibleWhen = field.visibleWhen;

  if (visibleWhen) {
    const watchedValue = watch(visibleWhen.field);
    if (watchedValue !== visibleWhen.value) {
      return null;
    }
  }

  return controlsMap[field.type](field, defaultValue);
}

export function FormRenderer<
  DATA extends Record<string, any> = Record<string, any>,
>({
  submitButtonLabel = 'Submit',
  serverErrors,
  ...props
}: FormRendererProps<DATA>) {
  const hasConditionalFields = props.form.some((f) => f.visibleWhen);

  const initialValues = useMemo(() => {
    const inlineSelectDefaults = props.form.reduce((acc, field) => {
      if (field.type === FieldType.InlineSelect && 'options' in field && field.options?.length) {
        acc[field.name] = field.options[0].value;
      }
      return acc;
    }, {} as Record<string, any>);
    return { ...inlineSelectDefaults, ...props.values } as DATA;
  }, [props.form, props.values]);

  const schema = useMemo(
    () =>
      z.object(
        props.form.reduce((schema, field) => {
          if (field.visibleWhen) {
            schema[field.name] = getZodRule(field.type).optional().or(z.literal(''));
          } else {
            schema[field.name] = getZodRule(field.type);
          }
          return schema;
        }, {} as Record<string, z.ZodTypeAny>),
      ),
    [props.form],
  ) as unknown as ZodType<DATA>;

  return (
    <Form<DATA>
      onSubmit={props.onSubmit}
      schema={schema}
      initialValues={initialValues}
      className={props.className}
      serverErrors={serverErrors ?? []}
    >
      <div
        className={classNames(
          props.formBodyClassName ? props.formBodyClassName : 'space-y-5',
        )}
        data-test-id={props['data-test-id']}
      >
        {props.form.map((field) => (
          <div key={`${field.name}-${field.label}`}>
            {hasConditionalFields ? (
              <ConditionalField
                field={field}
                defaultValue={
                  props.values ? props.values[field.name] : undefined
                }
              />
            ) : (
              controlsMap[field.type](
                field,
                props.values ? props.values[field.name] : undefined,
              )
            )}
          </div>
        ))}
      </div>
      <Button
        className="mt-8 sm:mt-10"
        type="submit"
        data-test-id="submit"
        style={{ backgroundColor: props.color }}
        loading={props.isLoading}
        guided-tour-id="submit-button"
      >
        {submitButtonLabel}
      </Button>
    </Form>
  );
}
